/**
 * Reading Crossref's update notices for a work: retractions, expressions of concern,
 * corrections, withdrawals.
 *
 * Crossref lists them on the work as `updated-by`. Each notice names its own DOI, a
 * `type`, the date, and a `source`: `publisher` for the publisher's own Crossmark
 * deposit, `retraction-watch` for the Retraction Watch database, which Crossref
 * acquired in September 2023 and opened to everyone. The two often record the same
 * notice, and sometimes disagree about it — Retraction Watch counts the 2012 notice on
 * Joly, Stapel and Lindenberg (2008) as a retraction, the publisher deposited it as a
 * correction — so a notice is merged only when both its DOI and its type match.
 *
 * Publisher types are free text in practice ("Erratum", "expression-of-concern",
 * "retration"), so they are folded to a few kinds before anything is decided.
 */

import { plainText } from "@/lib/tool-kit/identifiers/bibtex";
import type {
  CrossrefDate,
  CrossrefUpdate,
  CrossrefWork,
} from "@/lib/tool-kit/identifiers/lookup";

export type NoticeKind =
  | "retraction"
  | "withdrawal"
  | "removal"
  | "partial-retraction"
  | "concern"
  | "correction"
  | "other";

/** The verdict for a work, from its most serious notice. */
export type WorkStatus =
  | "retracted"
  | "withdrawn"
  | "partially-retracted"
  | "concern"
  | "corrected"
  | "updated"
  | "none"
  /** The DOI is itself a notice — a retraction notice, an erratum — for another work. */
  | "notice";

export interface Notice {
  kind: NoticeKind;
  /** Crossref's type, folded: "erratum", "new_version", "expression_of_concern". */
  type: string;
  /** Crossref's label for the type, as Crossref wrote it ("New version"). */
  label: string;
  /** The notice's DOI — for a "notice" status, the DOI of the work it updates. */
  doi: string;
  /** [year, month, day], as many parts as Crossref gives; empty when it gives none. */
  date: number[];
  /** "publisher", "retraction-watch", or whatever else Crossref reports. */
  sources: string[];
  /** Retraction Watch record numbers. */
  recordIds: string[];
  /** The notice was published at the work's own DOI, replacing the article page. */
  sameDoi: boolean;
}

export interface WorkAssessment {
  status: WorkStatus;
  title: string;
  container: string;
  year?: number;
  notices: Notice[];
}

const RE_SEPARATORS = /[\s-]+/g;

const KIND_BY_TYPE = new Map<string, NoticeKind>(
  Object.entries({
    retraction: "retraction",
    retracted: "retraction",
    // Two publisher deposits misspell it; Crossref's facet counts list both.
    retration: "retraction",
    retracion: "retraction",
    partial_retraction: "partial-retraction",
    withdrawal: "withdrawal",
    withdrawn: "withdrawal",
    removal: "removal",
    removed: "removal",
    expression_of_concern: "concern",
    correction: "correction",
    corrected: "correction",
    corrected_article: "correction",
    corrigendum: "correction",
    erratum: "correction",
    err: "correction",
  } satisfies Record<string, NoticeKind>)
);

const SEVERITY: Record<NoticeKind, number> = {
  retraction: 6,
  withdrawal: 5,
  removal: 5,
  "partial-retraction": 4,
  concern: 3,
  correction: 2,
  other: 1,
};

const STATUS_OF_KIND: Record<NoticeKind, WorkStatus> = {
  retraction: "retracted",
  withdrawal: "withdrawn",
  removal: "withdrawn",
  "partial-retraction": "partially-retracted",
  concern: "concern",
  correction: "corrected",
  other: "updated",
};

/** "Expression-of-Concern" → "expression_of_concern". */
export function foldUpdateType(type: string): string {
  return type.trim().toLowerCase().replace(RE_SEPARATORS, "_");
}

export function noticeKind(type: string): NoticeKind {
  return KIND_BY_TYPE.get(foldUpdateType(type)) ?? "other";
}

function dateParts(date: CrossrefDate | undefined): number[] {
  const parts = date?.["date-parts"]?.[0] ?? [];
  const clean: number[] = [];
  for (const part of parts) {
    if (typeof part !== "number") {
      break;
    }
    clean.push(part);
  }
  return clean;
}

/** -1, 0 or 1; an undated notice sorts last. */
function compareDates(a: number[], b: number[]): number {
  if (a.length === 0 || b.length === 0) {
    return b.length - a.length;
  }
  for (let index = 0; index < Math.max(a.length, b.length); index += 1) {
    const difference = (a[index] ?? 0) - (b[index] ?? 0);
    if (difference !== 0) {
      return Math.sign(difference);
    }
  }
  return 0;
}

/**
 * Merge Crossref's entries into notices — one per DOI and type, with every source that
 * recorded it and the earliest date any of them gave — in date order.
 */
export function mergeNotices(
  updates: readonly CrossrefUpdate[],
  ownDoi: string
): Notice[] {
  const byKey = new Map<string, Notice>();
  for (const update of updates) {
    const doi = update.DOI?.trim() ?? "";
    const type = foldUpdateType(update.type ?? "");
    if (doi === "" && type === "") {
      continue;
    }
    const key = `${doi.toLowerCase()}|${type}`;
    const date = dateParts(update.updated);
    const existing = byKey.get(key);
    const notice: Notice = existing ?? {
      kind: noticeKind(type),
      type,
      label: plainText(update.label ?? "") || type.replaceAll("_", " "),
      doi,
      date,
      sources: [],
      recordIds: [],
      sameDoi: doi !== "" && doi.toLowerCase() === ownDoi.toLowerCase(),
    };
    if (update.source && !notice.sources.includes(update.source)) {
      notice.sources.push(update.source);
    }
    const recordId = update["record-id"];
    if (recordId && !notice.recordIds.includes(recordId)) {
      notice.recordIds.push(recordId);
    }
    if (compareDates(date, notice.date) < 0) {
      notice.date = date;
    }
    byKey.set(key, notice);
  }
  return [...byKey.values()].sort(
    (a, b) =>
      compareDates(a.date, b.date) || SEVERITY[b.kind] - SEVERITY[a.kind]
  );
}

/** The status the most serious notice gives; "none" when there is no notice. */
export function statusOf(notices: readonly Notice[]): WorkStatus {
  let worst: Notice | undefined;
  for (const notice of notices) {
    if (!worst || SEVERITY[notice.kind] > SEVERITY[worst.kind]) {
      worst = notice;
    }
  }
  return worst ? STATUS_OF_KIND[worst.kind] : "none";
}

/**
 * Everything the checker reports for one Crossref record. A work with no notices of
 * its own that names other works in `update-to` is a notice itself; a notice published
 * at its own DOI (an article page replaced by "WITHDRAWN") is not.
 */
export function assessWork(work: CrossrefWork): WorkAssessment {
  const doi = work.DOI ?? "";
  const notices = mergeNotices(work["updated-by"] ?? [], doi);
  const base = {
    title: plainText(work.title?.[0] ?? ""),
    container: plainText(work["container-title"]?.[0] ?? ""),
    year: dateParts(work.issued)[0],
  };
  if (notices.length > 0) {
    return { ...base, status: statusOf(notices), notices };
  }
  const targets = mergeNotices(work["update-to"] ?? [], doi).filter(
    (notice) => !notice.sameDoi
  );
  if (targets.length > 0) {
    return { ...base, status: "notice", notices: targets };
  }
  return { ...base, status: "none", notices: [] };
}

/* -------------------------------------------------------------- output */

/** A notice date the way the reader's language writes it: "Sep 18, 2012", "Feb 2004". */
export function formatNoticeDate(
  parts: readonly number[],
  locale: string
): string {
  const [year, month, day] = parts;
  if (!year) {
    return "";
  }
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    timeZone: "UTC",
  };
  if (month) {
    options.month = "short";
    if (day) {
      options.day = "numeric";
    }
  }
  return new Intl.DateTimeFormat(locale, options).format(
    new Date(Date.UTC(year, (month ?? 1) - 1, day ?? 1))
  );
}

/** 2012-09-18, 2012-09 or 2012 — for the CSV, where a spreadsheet should read it. */
export function isoNoticeDate(parts: readonly number[]): string {
  return parts
    .slice(0, 3)
    .map((part, index) => String(part).padStart(index === 0 ? 4 : 2, "0"))
    .join("-");
}

const RE_CSV_SPECIAL = /[",\r\n]/;

/** Rows as CSV, quoting only the cells that need it. */
export function toCsv(rows: readonly (readonly string[])[]): string {
  const cell = (value: string) =>
    RE_CSV_SPECIAL.test(value) ? `"${value.replaceAll('"', '""')}"` : value;
  return rows.map((row) => `${row.map(cell).join(",")}\r\n`).join("");
}
